import { Box, Grid, Typography } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { FieldError, Option } from '@spike/model';
import { Button, Checkbox, PercentageField2 } from 'components/UI';
import { useApiClientWrapper, useTimeZone } from 'hooks';
import { FunctionComponent, memo, useCallback, useMemo, useState } from 'react';
import { reduceResolution, wbp } from 'Theme';
import Info from './Info';
import moment from 'moment';
import { saveThunk } from '@spike/commissions-action';
import { useDispatch, useSelector } from 'react-redux';
import { Commission, CommissionPlan } from '@spike/commission-model';
import { RootState } from 'store';
import { v4 as uuid } from 'uuid';
import { Staff } from 'model/Staff';
import { validate } from '../CommissionValidation';
import { showErrorThunk } from '@spike/notifications-action/lib/esm/action/NotificationsActions';
import { sortCommissionCategories } from './commissionCategoryOrder';

interface AddCommissionsProps {
    title: string;
    onSave: () => void;
    onClose: () => void;
    saving?: boolean;
    member?: Staff;
    commissionPlan?: CommissionPlan;
}

interface CommissionRow {
    checked: boolean;
    rate: string;
    uuid: string;
}

const DEFAULT_CADENCE = { id: 'daily', name: 'daily', days: 1 };

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        container: {
            flex: 1,
            width: '100%',
            display: 'flex',
            flexWrap: 'nowrap',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            minHeight: 0,
            overflow: 'hidden'
        },
        header: {
            width: '100%',
            flexShrink: 0,
            padding: '10px 16px 0px',

            [theme.breakpoints.up('md')]: {
                padding: '33px 32px 0px'
            }
        },
        titleFont: {
            color: '#000',
            fontWeight: 600,
            marginBottom: 25,
            marginTop: 0,

            [theme.breakpoints.down(wbp)]: {
                fontSize: `${reduceResolution(25)}px`,
                lineHeight: `${reduceResolution(36)}px`,
                marginBottom: `${reduceResolution(40)}px`
            },
            [theme.breakpoints.up(wbp)]: {
                fontSize: '25px',
                lineHeight: '36px'
            }
        },
        subtitle: {
            color: '#000',
            fontWeight: 600,
            marginBottom: 6,
            [theme.breakpoints.down(wbp)]: {
                fontSize: `${reduceResolution(20)}px`,
                lineHeight: `${reduceResolution(18)}px`
            },
            [theme.breakpoints.up(wbp)]: {
                fontSize: '18px',
                lineHeight: '18px'
            }
        },
        date: {
            color: '#000',
            fontWeight: 500,
            marginBottom: 20,
            [theme.breakpoints.down(wbp)]: {
                fontSize: `${reduceResolution(19)}px`,
                lineHeight: `${reduceResolution(15)}px`,
                marginBottom: `${reduceResolution(40)}px`
            },
            [theme.breakpoints.up(wbp)]: {
                fontSize: '15px',
                lineHeight: '15px'
            }
        },
        appliesTo: {
            color: '#000',
            fontWeight: 600,
            marginTop: 28,
            marginBottom: 6,
            [theme.breakpoints.down(wbp)]: {
                fontSize: `${reduceResolution(20)}px`,
                lineHeight: `${reduceResolution(24)}px`
            },
            [theme.breakpoints.up(wbp)]: {
                fontSize: '18px',
                lineHeight: '24px'
            }
        },
        list: {
            width: '100%',
            flex: 1,
            overflowY: 'auto',
            minHeight: 0,
            padding: '0px 16px',
            [theme.breakpoints.up('md')]: {
                padding: '0px 32px'
            }
        },
        footer: {
            width: '100%',
            display: 'flex',
            flexShrink: 0,
            padding: '24px 16px 20px',
            borderTop: '1px solid #DDDDDD',

            [theme.breakpoints.up('md')]: {
                padding: '20px 40px'
            }
        },
        button: {
            'width': '100%',
            'height': 54,
            'borderRadius': 30,
            '& span': {
                fontSize: 18,
                fontWeight: 600
            }
        }
    })
);

const useCategoryRowStyles = makeStyles((theme: Theme) =>
    createStyles({
        row: {
            width: '100%',
            height: 60,
            boxSizing: 'border-box',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '1px solid #D3D3D3'
        },
        rowLabel: {
            display: 'flex',
            alignItems: 'center',
            gap: 12
        },
        commissionType: {
            color: '#000',
            fontWeight: 500,
            [theme.breakpoints.down(wbp)]: {
                fontSize: `${reduceResolution(18)}px`,
                lineHeight: `${reduceResolution(18)}px`
            },
            [theme.breakpoints.up(wbp)]: {
                fontSize: '16px',
                lineHeight: '18px'
            }
        },
        field: {
            'width': 135,
            '& input': {
                padding: '11px 14px',
                textAlign: 'center',
                fontSize: 16,
                fontWeight: 500
            },
            '& .MuiOutlinedInput-root': {
                borderRadius: 30
            },
            '& fieldset': {
                borderRadius: 30
            },
            '& .MuiInputAdornment-positionEnd': {
                marginLeft: 0
            }
        }
    })
);

interface CategoryRowProps {
    category: Option<string>;
    checked: boolean;
    rate: string;
    errors: Array<FieldError>;
    onToggle: (_categoryId: string) => void;
    onRate: (_categoryId: string, _value: number | undefined) => void;
}

function CategoryRowBase({ category, checked, rate, errors, onToggle, onRate }: CategoryRowProps) {
    const classes = useCategoryRowStyles();

    return (
        <Box className={classes.row}>
            <Box className={classes.rowLabel}>
                <Checkbox
                    checked={checked}
                    onChange={() => onToggle(category.id)}
                />
                <Typography className={classes.commissionType}>{category.name}</Typography>
            </Box>
            {checked && (
                <PercentageField2
                    value={rate === '' ? 0 : Number(rate)}
                    autoComplete="off"
                    onChange={value => onRate(category.id, value)}
                    required={true}
                    errors={errors}
                    className={classes.field}
                    alignRight={false}
                    precision={5}
                    scale={2}
                    max={100}
                />
            )}
        </Box>
    );
}

const CategoryRow = memo(CategoryRowBase);

export const AddCommissions: FunctionComponent<AddCommissionsProps> = props => {
    const classes = useStyles();

    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();
    const timeZone = useTimeZone();

    const availableCategoriesRaw = useSelector<RootState, Array<Option<string>>>(
        state => state.commissions.availableCategories ?? []
    );

    const availableCategories = useMemo(
        () => sortCommissionCategories(availableCategoriesRaw),
        [availableCategoriesRaw]
    );

    const [errors, setErrors] = useState<Array<FieldError>>([]);
    const [rows, setRows] = useState<Record<string, CommissionRow>>(() => {
        const initialRows: Record<string, CommissionRow> = {};
        availableCategories.forEach(category => {
            const existing = props.commissionPlan?.commissions.find(
                commission => commission.category.id === category.id
            );
            initialRows[category.id] = {
                checked: Boolean(existing),
                rate: existing ? existing.rate : '',
                uuid: existing?.uuid ?? uuid()
            };
        });
        return initialRows;
    });

    const staffId = props.member!.id;
    const effectiveDate = useMemo(() => moment().tz(timeZone).format('MMM DD, YYYY'), [timeZone]);
    const effectiveTime = useMemo(() => moment().tz(timeZone).format('hh:mm a'), [timeZone]);
    const hasSelection = useMemo(() => Object.values(rows).some(row => row.checked), [rows]);

    const toggleHandler = useCallback((categoryId: string) => {
        setRows(current => ({
            ...current,
            [categoryId]: {
                ...current[categoryId],
                checked: !current[categoryId]?.checked
            }
        }));
    }, []);

    const rateHandler = useCallback((categoryId: string, value: number | undefined) => {
        setRows(current => ({
            ...current,
            [categoryId]: {
                ...current[categoryId],
                rate: value === undefined ? '' : String(value)
            }
        }));
    }, []);

    const saveHandler = useCallback(() => {
        const commissions: Array<Commission> = availableCategories
            .filter(category => rows[category.id]?.checked)
            .map(category => ({
                uuid: rows[category.id].uuid,
                minimum: '0.00',
                maximum: null,
                rate: rows[category.id].rate === '' ? '0.0' : rows[category.id].rate,
                category: { id: category.id, name: category.name },
                cadence: { ...DEFAULT_CADENCE }
            }));

        const commissionPlanAux: CommissionPlan = {
            uuid: uuid(),
            from: moment().tz(timeZone),
            to: null,
            staffId: staffId!,
            commissions,
            active: true
        };

        const validationErrors = validate(commissionPlanAux);
        setErrors(validationErrors);

        if (validationErrors.length === 0) {
            if (commissions.length > 0) {
                dispatch(saveThunk(apiClientWrapper, commissionPlanAux));
            }
            props.onSave && props.onSave();
        } else {
            validationErrors.forEach(error => dispatch(showErrorThunk(error.errorMessage)));
        }
    }, [availableCategories, rows, timeZone, staffId, apiClientWrapper, props.onSave]);

    return (
        <Grid
            container
            className={classes.container}
        >
            <Box className={classes.header}>
                <Typography className={classes.titleFont}>{props.title}</Typography>

                <Typography className={classes.subtitle}>Effective Date</Typography>
                <Typography className={classes.date}>{`${effectiveDate} at ${effectiveTime}`}</Typography>

                <Info description={'Commissions rates will apply from this moment on.'} />

                <Typography className={classes.appliesTo}>Commission applies to</Typography>
            </Box>

            <Box className={classes.list}>
                {availableCategories.map(category => {
                    const row = rows[category.id];
                    return (
                        <CategoryRow
                            key={category.id}
                            category={category}
                            checked={Boolean(row?.checked)}
                            rate={row?.rate ?? ''}
                            errors={errors}
                            onToggle={toggleHandler}
                            onRate={rateHandler}
                        />
                    );
                })}
            </Box>

            <Box className={classes.footer}>
                <Button
                    className={classes.button}
                    label="Save"
                    color="green"
                    loading={props.saving}
                    disabled={!hasSelection}
                    onClick={saveHandler}
                />
            </Box>
        </Grid>
    );
};

export default AddCommissions;
